// Bootstrap impersonate mode + storage guard sono ora installati via inline <script> in
// index.html (PRIMA di qualunque modulo ESM, vedi commento in index.html). Questo import
// rimane come safety net idempotente — la funzione no-op se l'inline ha già installato.
import { installImpersonateStorageGuard } from './lib/utils/impersonateStorageGuard';
installImpersonateStorageGuard();

// Disabilita console.log in produzione (DEVE essere il primo import)
import './lib/utils/disableConsoleProd';

// Service worker gestito da VitePWA con autoUpdate + skipWaiting
// (rimossa deregistrazione manuale che causava race condition con il SW della PWA)

// Sentry initialization (must be before other imports)
import * as Sentry from '@sentry/react';

Sentry.init({
  dsn: "https://65341bedb6814be36abb6aff6997a7ce@o4510658858713088.ingest.de.sentry.io/4510658860154960",
  environment: import.meta.env.MODE,
  // Solo in produzione; spento nell'ambiente di prova (VITE_TELEMETRY_DISABLED, dev.flusia.it).
  enabled: import.meta.env.PROD && import.meta.env.VITE_TELEMETRY_DISABLED !== 'true',
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 0.2, // Capture 20% of transactions (cost control)
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.flusia\.it/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // 10% of sessions
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
  // Enable logs
  _experiments: {
    enableLogs: true,
  },
});

// Bug report: buffer circolare ultimi 10 console.error
const __bugReportErrors: string[] = [];
const _origConsoleError = console.error;
console.error = (...args: unknown[]) => {
  __bugReportErrors.push(new Date().toISOString() + ' ' + args.map(String).join(' '));
  if (__bugReportErrors.length > 10) __bugReportErrors.shift();
  _origConsoleError.apply(console, args);
};
(window as any).__bugReportErrors = __bugReportErrors;

// Error interceptor: toast con "Segnala bug" per errori runtime
import React from 'react';
import hotToast from 'react-hot-toast';
// Quali errori meritano l'avviso con "Segnala" (lista ignorati, rete/sessione, script esterni)
import { classifyAutoReportError, stackForReport } from './lib/errors/autoReportFilter';

let _lastErrorToastTime = 0;

function ErrorToast({ t, errorMsg, truncated, errorStack }: { t: { id: string }; errorMsg: string; truncated: string; errorStack?: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, maxWidth: 420,
      padding: '12px 14px', background: '#fff', borderRadius: 10,
      border: '1px solid rgba(55,53,47,0.09)', borderLeft: '3px solid #EB5757',
      boxShadow: 'rgba(55,53,47,0.08) 0px 3px 10px',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#37352F', marginBottom: 2 }}>
          Si è verificato un errore
        </div>
        <div style={{ fontSize: 11, color: '#787774', wordBreak: 'break-word' }}>
          {truncated}
        </div>
      </div>
      <button
        onClick={() => {
          hotToast.dismiss(t.id);
          window.dispatchEvent(new CustomEvent('open-bug-report', {
            detail: { errorMessage: errorMsg, errorStack }
          }));
        }}
        style={{
          flexShrink: 0, padding: '4px 10px', fontSize: 11, fontWeight: 500,
          color: '#fff', background: '#EB5757', border: 'none', borderRadius: 6,
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        Segnala
      </button>
    </div>
  );
}

function showErrorToast(errorMsg: string, errorStack?: string) {
  const now = Date.now();
  if (now - _lastErrorToastTime < 10000) return; // throttle 10s
  _lastErrorToastTime = now;

  const truncated = errorMsg.length > 120 ? errorMsg.slice(0, 120) + '...' : errorMsg;

  hotToast.custom(
    (t) => <ErrorToast t={t} errorMsg={errorMsg} truncated={truncated} errorStack={errorStack} />,
    { duration: 8000 }
  );
}

/** Avviso solo per errori del CRM; rete/sessione passeggere ed errori esterni finiscono in console. */
function gestisciErroreNonGestito(msg: string, error: unknown, filename?: string): void {
  const rawStack = (error as { stack?: unknown } | null | undefined)?.stack;
  const stack = typeof rawStack === 'string' ? rawStack : undefined;
  const decisione = classifyAutoReportError({ message: msg, stack, filename, origin: window.location.origin });
  if (decisione === 'ignored') return;
  if (decisione !== 'report') {
    console.warn(`[errore non segnalato: ${decisione === 'transient' ? 'rete/sessione' : 'origine esterna'}]`, msg);
    return;
  }
  setTimeout(() => showErrorToast(msg, stackForReport(stack)), 0);
}

// Rete di sicurezza dopo un deploy: la scheda aperta gira col codice vecchio e
// i suoi file non esistono piu' sul server, quindi al primo caricamento pigro
// la pagina resta bianca in silenzio (questi errori sono nella lista ignorati).
// Qui si ricarica UNA volta: l'utente si ritrova la versione nuova invece del
// vuoto. In condizioni normali non scatta, perche' il service worker continua a
// servire la versione vecchia e l'avviso "Nuova versione" chiede il permesso.
const ERRORI_FILE_MANCANTE = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'ChunkLoadError',
  'Loading chunk',
  'Importing a module script failed',
];
const CHIAVE_RICARICA = 'flusia_ricarica_versione';

function ricaricaSeVersioneVecchia(msg: string): void {
  if (!ERRORI_FILE_MANCANTE.some((e) => msg.includes(e))) return;
  try {
    const ultima = Number(sessionStorage.getItem(CHIAVE_RICARICA) || 0);
    // Gia' ricaricato da poco: il problema e' un altro, non entrare in ciclo.
    if (Date.now() - ultima < 60_000) return;
    sessionStorage.setItem(CHIAVE_RICARICA, String(Date.now()));
  } catch {
    return; // senza sessionStorage non si puo' garantire "una volta sola"
  }
  window.location.reload();
}

window.addEventListener('error', (event) => {
  const msg = event.message || String(event.error);
  ricaricaSeVersioneVecchia(msg);
  gestisciErroreNonGestito(msg, event.error, event.filename);
});

window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message || String(event.reason);
  ricaricaSeVersioneVecchia(msg);
  gestisciErroreNonGestito(msg, event.reason);
});

import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App.tsx';
// Font di marca (Inter variabile, self-hosted): vedi fontFamily in tailwind.config.js
import '@fontsource-variable/inter';
import './index.css';

// Import and initialize debug settings
import './lib/utils/debug';

// Campi numerici che partono da 0: il primo tasto sostituisce lo 0 invece di finirgli dietro («0350»)
import { installNumberInputZeroFix } from './lib/utils/numberInputZero';
installNumberInputZeroFix();

// Initialize workflow and notification trigger services (solo CRM, non nei portali)
import { workflowTriggerService } from './lib/events/workflowTriggerService';
import { notificationTriggerService } from './lib/events/notificationTriggerService';

const isPortalPath = window.location.pathname.startsWith('/client-portal') ||
                     window.location.pathname.startsWith('/partner-portal') ||
                     window.location.pathname.startsWith('/collaborator-portal');

if (!isPortalPath) {
  workflowTriggerService.init();
  notificationTriggerService.init();

  // Product analytics solo per lo staff che usa il CRM, mai per i portali esterni
  // (sono i clienti/partner dei nostri clienti, non li tracciamo).
  // Il lazy-load NON deve mai diventare una unhandled rejection: dopo un deploy i
  // client con l'index.html vecchio in cache tentano un chunk con hash superato
  // (404 → "Failed to fetch dynamically imported module"). L'analytics è opzionale:
  // se il chunk non c'è, si ignora in silenzio invece di finire su Sentry.
  import('./lib/analytics/posthogClient').then(({ initPostHog }) => initPostHog()).catch(() => {});

  // Traduce gli eventi di dominio già emessi dagli store (stessi consumati sopra
  // da workflowTriggerService/notificationTriggerService) in eventi di prodotto PostHog.
  import('./lib/analytics/posthogEventBridge').then(({ initPostHogEventBridge }) => initPostHogEventBridge()).catch(() => {});
}

// Import ThemeProvider for light/dark mode
import { ThemeProvider } from './contexts/ThemeContext';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </ThemeProvider>
);
